@import './forced-colors.css';

/**
 * Interaction fill — selected (`::before`) + hover/press (`::after`) overlays.
 *
 * Paint translucent interaction tokens *over* the control’s own background —
 * never by swapping `background-color` on `:hover` / `:active`, and never via
 * `color-mix(bg, fg)` for control chrome.
 *
 * Paint order (bottom → top):
 *   1. Element background (intent / surface)
 *   2. `::before` — selected / active (opt-in via `.ds-interaction-fill--selected`)
 *   3. Label / icon / badge content (`.ds-interaction-fill__content` or z-index: 2)
 *   4. `::after` — hover / press wash + optional inset border + inset focus ring
 *
 * Apply `.ds-interaction-fill` on the interactive element (host or inner row).
 * Omit `.ds-interaction-fill--selected` when chrome wants icon-color-only
 * selection (`activeFill={false}` on unfilled icon buttons).
 *
 * Fills use positive z-index so they paint *above* an opaque host background
 * (Chip, ButtonFilled). Content sits under the hover wash so badge dots are
 * covered; raise label/icon with `.ds-interaction-fill__content` (z-index: 2)
 * — never above `::after` (z-index: 3).
 *
 * Inset borders must paint on `::after` (not the element’s own `box-shadow`)
 * so the stroke stays visible above selected / hover washes. Set
 * `--ds-interaction-border-*` or use `.ds-interaction-fill--bordered`.
 * Do not use a layout `border` on interaction targets.
 *
 * If the control uses `all: unset`, re-assert `position: relative; z-index: 0`
 * after the unset so this util’s stacking context still applies.
 *
 * Surface helpers remap tokens + `--ds-focus-ring-color`:
 *   `.ds-interaction-fill--on-faint|medium|bold|strong|translucent|inverted|media|always-dark|navigation`
 *
 * Override hooks: `--ds-interaction-hover`, `--ds-interaction-pressed`,
 * `--ds-interaction-active`, `--ds-interaction-border-color`,
 * `--ds-interaction-border-width`, `--ds-interaction-divider-color`, and
 * `--ds-interaction-divider-width`, plus the corresponding inline-start/end
 * divider hooks.
 *
 * @see AGENTS.md — "Interaction fill"
 */

:host(.ds-interaction-fill),
.ds-interaction-fill {
  position: var(--ds-interaction-position, relative);
  z-index: var(--ds-interaction-z-index, 0);
  cursor: default;
  --ds-interaction-hover: var(--color-interaction-hover);
  --ds-interaction-pressed: var(--color-interaction-pressed);
  --ds-interaction-active: var(--color-interaction-active-brand);
  /* Inset stroke on ::after — default off (transparent / zero width). */
  --ds-interaction-border-width: 0px;
  --ds-interaction-border-color: transparent;
  --ds-interaction-divider-width: 0px;
  --ds-interaction-divider-color: transparent;
  --ds-interaction-divider-inline-start-width: 0px;
  --ds-interaction-divider-inline-start-color: transparent;
  --ds-interaction-divider-inline-end-width: 0px;
  --ds-interaction-divider-inline-end-color: transparent;
}

:host(.ds-interaction-fill)::before,
.ds-interaction-fill::before,
:host(.ds-interaction-fill)::after,
.ds-interaction-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: transparent;
  pointer-events: none;
  cursor: inherit;
  transition: none;
}

/* Layer stack (bottom → top): selected → content → hover/press + border (topmost). */
:host(.ds-interaction-fill)::before,
.ds-interaction-fill::before {
  z-index: 1;
}

:host(.ds-interaction-fill)::after,
.ds-interaction-fill::after {
  z-index: 3;
  /* Border above selected (::before) and this pseudo’s own hover wash. */
  box-shadow:
    inset 0 calc(-1 * var(--ds-interaction-divider-width)) 0 var(--ds-interaction-divider-color),
    inset var(--ds-interaction-divider-inline-start-width) 0 0
      var(--ds-interaction-divider-inline-start-color),
    inset calc(-1 * var(--ds-interaction-divider-inline-end-width)) 0 0
      var(--ds-interaction-divider-inline-end-color),
    inset 0 0 0 var(--ds-interaction-border-width) var(--ds-interaction-border-color);
}

/* Opt-in secondary inset border (override color via `--ds-interaction-border-color`). */
:host(.ds-interaction-fill--bordered),
.ds-interaction-fill--bordered {
  --ds-interaction-border-width: var(--dimension-stroke-width-012);
  --ds-interaction-border-color: var(--color-border-secondary);
}

:host(.ds-interaction-fill--selected)::before,
.ds-interaction-fill--selected::before {
  background: var(--ds-interaction-active);
}

/*
 * Notification-dot halo: when selected fill is on, set `--ds-interaction-dot-ring`
 * to the active wash so rings can match. Prefer also remapping the consumer’s
 * surface ring token (e.g. `--ds-button-unfilled-dot-ring`) or passing
 * `background="var(--ds-interaction-active)"` from the parent when selected —
 * custom-property fallbacks through nested scoped hosts are easy to miss.
 * Omit `--selected` (chrome `activeFill={false}`) to keep the surface ring.
 */
:host(.ds-interaction-fill--selected),
.ds-interaction-fill--selected {
  --ds-interaction-dot-ring: var(--ds-interaction-active);
}

/*
 * Touch browsers can latch :hover after a tap. Only hover-capable fine
 * pointers receive the hover wash; touch still receives :active press feedback.
 */
@media (hover: hover) and (pointer: fine) {
  :host(.ds-interaction-fill:hover:not(:disabled))::after,
  .ds-interaction-fill:hover:not(:disabled)::after {
    background: var(--ds-interaction-hover);
  }
}

:host(.ds-interaction-fill:active:not(:disabled))::after,
.ds-interaction-fill:active:not(:disabled)::after {
  background: var(--ds-interaction-pressed);
}

/* A parent row/list may coordinate one fill across several sibling targets. */
:host(.ds-interaction-fill--grouped)::after,
.ds-interaction-fill--grouped::after,
:host(.ds-interaction-fill--grouped:active:not(:disabled))::after,
.ds-interaction-fill--grouped:active:not(:disabled)::after {
  background: var(--ds-interaction-group-fill, transparent);
}

@media (hover: hover) and (pointer: fine) {
  :host(.ds-interaction-fill--grouped:hover:not(:disabled))::after,
  .ds-interaction-fill--grouped:hover:not(:disabled)::after {
    background: var(--ds-interaction-group-fill, transparent);
  }
}

:host(.ds-interaction-fill--grouped),
.ds-interaction-fill--grouped {
  --ds-interaction-divider-width: var(--ds-interaction-group-divider-width, 0px);
  --ds-interaction-divider-color: var(--ds-interaction-group-divider-color, transparent);
  --ds-interaction-divider-inline-start-width: var(
    --ds-interaction-group-divider-inline-start-width,
    0px
  );
  --ds-interaction-divider-inline-start-color: var(
    --ds-interaction-group-divider-inline-start-color,
    transparent
  );
  --ds-interaction-divider-inline-end-width: var(
    --ds-interaction-group-divider-inline-end-width,
    0px
  );
  --ds-interaction-divider-inline-end-color: var(
    --ds-interaction-group-divider-inline-end-color,
    transparent
  );
}

/* Content under the hover wash (so badge dots are covered by ::after). */
:host(.ds-interaction-fill) > .ds-interaction-fill__content,
.ds-interaction-fill > .ds-interaction-fill__content,
.ds-interaction-fill__content {
  position: relative;
  z-index: 2;
  cursor: inherit;
}

/* ── Surface context ─────────────────────────────────────────────────────── */

:host(.ds-interaction-fill--on-faint),
.ds-interaction-fill--on-faint {
  --ds-interaction-active: var(--color-interaction-active);
}

:host(.ds-interaction-fill--on-medium),
.ds-interaction-fill--on-medium {
  --ds-interaction-hover: var(--color-interaction-on-medium-background-hover);
  --ds-interaction-pressed: var(--color-interaction-on-medium-background-pressed);
  --ds-interaction-active: var(--color-interaction-on-medium-background-active);
  --ds-focus-ring-color: var(--color-interaction-on-medium-background-focus);
}

:host(.ds-interaction-fill--on-bold),
.ds-interaction-fill--on-bold {
  --ds-interaction-hover: var(--color-interaction-on-bold-background-hover);
  --ds-interaction-pressed: var(--color-interaction-on-bold-background-pressed);
  --ds-interaction-active: var(--color-interaction-on-bold-background-active);
  --ds-focus-ring-color: var(--color-interaction-on-bold-background-focus);
}

:host(.ds-interaction-fill--on-strong),
.ds-interaction-fill--on-strong {
  --ds-interaction-hover: var(--color-interaction-on-strong-background-hover);
  --ds-interaction-pressed: var(--color-interaction-on-strong-background-pressed);
  --ds-interaction-active: var(--color-interaction-on-strong-background-active);
  --ds-focus-ring-color: var(--color-interaction-on-strong-background-focus);
}

:host(.ds-interaction-fill--on-translucent),
.ds-interaction-fill--on-translucent {
  --ds-interaction-hover: var(--color-translucent-interaction-hover);
  --ds-interaction-pressed: var(--color-translucent-interaction-pressed);
  --ds-interaction-active: var(--color-translucent-interaction-active-brand);
  --ds-focus-ring-color: var(--color-translucent-interaction-focus);
}

:host(.ds-interaction-fill--on-inverted),
.ds-interaction-fill--on-inverted {
  --ds-interaction-hover: var(--color-inverted-interaction-hover);
  --ds-interaction-pressed: var(--color-inverted-interaction-pressed);
  --ds-interaction-active: var(--color-inverted-interaction-active-brand);
  --ds-focus-ring-color: var(--color-inverted-interaction-focus);
}

:host(.ds-interaction-fill--on-media),
.ds-interaction-fill--on-media {
  --ds-interaction-hover: var(--color-media-interaction-hover);
  --ds-interaction-pressed: var(--color-media-interaction-pressed);
  --ds-interaction-active: var(--color-media-interaction-active);
  --ds-focus-ring-color: var(--color-media-interaction-focus);
}

:host(.ds-interaction-fill--on-always-dark),
.ds-interaction-fill--on-always-dark {
  --ds-interaction-hover: var(--color-always-dark-interaction-hover);
  --ds-interaction-pressed: var(--color-always-dark-interaction-pressed);
  --ds-interaction-active: var(--color-always-dark-interaction-active-brand);
  --ds-focus-ring-color: var(--color-always-dark-interaction-focus);
}

:host(.ds-interaction-fill--on-navigation),
.ds-interaction-fill--on-navigation {
  --ds-interaction-hover: var(--color-navigation-interaction-hover);
  --ds-interaction-pressed: var(--color-navigation-interaction-pressed);
  --ds-interaction-active: var(--color-navigation-interaction-active);
  --ds-focus-ring-color: var(--color-navigation-interaction-focus);
}

@media (forced-colors: active) {
  /*
   * Forced-colors removes box-shadows and remaps translucent fills. Restore
   * boundaries with outlines and make selection a non-color-only inset ring.
   * Focus-ring selectors remain more specific and replace this outline while
   * keyboard focus is visible.
   */
  :host(.ds-interaction-fill)::after,
  .ds-interaction-fill::after {
    box-shadow: none;
    outline:
      var(--ds-interaction-border-width) solid
      var(--ds-forced-color-control-boundary);
    outline-offset: calc(-1 * var(--ds-interaction-border-width));
  }

  :host(.ds-interaction-fill--selected)::before,
  .ds-interaction-fill--selected::before {
    background: transparent;
    outline: var(--dimension-stroke-width-025) solid var(--ds-forced-color-selected);
    outline-offset: calc(-1 * var(--dimension-stroke-width-025));
  }
}

@media (forced-colors: active) and (hover: hover) and (pointer: fine) {
  :host(.ds-interaction-fill:hover:not(:disabled):not(:focus-visible):not(:focus-within):not(.ds-focus-ring--visible))::after,
  .ds-interaction-fill:hover:not(:disabled):not(:focus-visible):not(:focus-within):not(.ds-focus-ring--visible)::after {
    outline-color: var(--ds-forced-color-selected);
    outline-width: var(--dimension-stroke-width-012);
  }
}
